import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  useProfile,
  useUpdateProfile,
  useChangePassword,
  useSubscription,
  useSubscriptionUsage,
} from "../hooks/useApi";
import Card from "../components/Card";
import Button from "../components/Button";
import Badge from "../components/Badge";
import VerificationModal from "../components/VerificationModal";
import SubscriptionHistoryDrawer from "../components/SubscriptionHistoryDrawer";
import FixedPlanIcon from "../components/FixedPlanIcon";
import { getPersonType, type PersonType } from "../utils/profile";
import { formatNumber, toPersianDigits } from "../utils/format";

const inputClass =
  "w-full h-10 px-3 rounded-btn border border-line text-small text-text-secondary bg-surface focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all";

const inputErrorClass =
  "w-full h-10 px-3 rounded-btn border border-status-error-text text-small text-text-main bg-surface focus:outline-none focus:ring-2 focus:ring-status-error-text/30 transition-all";

// Persian alphabet (incl. ZWNJ and spaces)
const PERSIAN_TEXT_RE = /^[\u0621-\u064A\u0670-\u06D5\u200c\s]+$/;

function normalizeDigits(value: string): string {
  return value.replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d)));
}

function validatePersianText(value: string): string {
  if (!value) return "";
  if (!PERSIAN_TEXT_RE.test(value.trim())) return "فقط حروف فارسی مجاز است";
  return "";
}

function validateEmail(value: string): string {
  if (!value) return "";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "فرمت ایمیل صحیح نیست";
  return "";
}

function validatePhone(value: string): string {
  if (!value) return "";
  const normalized = normalizeDigits(value);
  if (!/^09\d{9}$/.test(normalized)) return "شماره موبایل باید با ۰۹ شروع شود و ۱۱ رقم باشد";
  return "";
}

function validateNationalCode(value: string): string {
  if (!value) return "";
  if (!/^\d{10}$/.test(normalizeDigits(value))) return "کد ملی باید دقیقاً ۱۰ رقم باشد";
  return "";
}

function validateNationalId(value: string): string {
  if (!value) return "";
  if (!/^\d{11}$/.test(normalizeDigits(value))) return "شناسه ملی باید دقیقاً ۱۱ رقم باشد";
  return "";
}

function validatePostalCode(value: string): string {
  if (!value) return "";
  if (!/^\d{10}$/.test(normalizeDigits(value))) return "کد پستی باید دقیقاً ۱۰ رقم باشد";
  return "";
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  dir = "rtl",
  ltr = false,
  error,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  dir?: string;
  ltr?: boolean;
  error?: string;
}) {
  return (
    <div>
      <label className="block text-small font-medium text-text-secondary mb-1.5">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`${error ? inputErrorClass : inputClass} ${ltr ? "ltr text-left font-number" : ""}`}
        dir={dir}
      />
      {error && <p className="text-small text-status-error-text mt-1.5">{error}</p>}
    </div>
  );
}

function PasswordField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="block text-small font-medium text-text-secondary mb-1.5">{label}</label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`${inputClass} pl-10`}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-main transition-colors"
          aria-label={show ? "مخفی کردن رمز" : "نمایش رمز"}
        >
          {show ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
              <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
              <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}

function VerifiedBox({
  verified,
  onClickVerify,
}: {
  verified: boolean;
  onClickVerify: () => void;
}) {
  return (
    <div className="flex items-center gap-2 mt-1.5">
      {verified ? (
        <Badge variant="success">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="ml-1">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          تأیید شده
        </Badge>
      ) : (
        <button
          type="button"
          onClick={onClickVerify}
          className="inline-flex items-center gap-1.5 text-small font-medium text-primary hover:text-primary-dark transition-colors"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          تأیید نشده — احراز هویت
        </button>
      )}
    </div>
  );
}

function DecorativeBanner() {
  return (
    <div
      aria-hidden="true"
      className="relative h-24 rounded-btn bg-primary/5 overflow-hidden"
    >
      <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-primary/10" />
      <div className="absolute top-3 right-24 w-9 h-9 rounded-full bg-primary/15" />
      <div className="absolute -bottom-12 left-12 w-36 h-36 rounded-full bg-primary/10" />
      <div className="absolute bottom-3 left-1/3 w-5 h-5 rounded-full bg-primary/20" />
      <div className="absolute top-4 left-8 w-3 h-3 rounded-full bg-primary/15" />
    </div>
  );
}

export default function Profile() {
  const navigate = useNavigate();
  const { data: user, isLoading } = useProfile();
  const { data: subData } = useSubscription();
  const update = useUpdateProfile();
  const changePassword = useChangePassword();

  const [form, setForm] = useState({
    email: "",
    name: "",
    family_name: "",
    phone: "",
    national_code: "",
    postal_code: "",
    person_type: "real" as PersonType,
    company_name: "",
    national_id: "",
  });

  const [verifyField, setVerifyField] = useState<"email" | "phone" | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const [pwdForm, setPwdForm] = useState({
    new: "",
    confirm: "",
  });

  useEffect(() => {
    if (user) {
      setForm({
        email: user.email || "",
        name: user.name || "",
        family_name: user.family_name || "",
        phone: user.phone || "",
        national_code: user.national_code || "",
        postal_code: user.postal_code || "",
        person_type: getPersonType(user),
        company_name: user.company_name || "",
        national_id: user.national_id || "",
      });
    }
  }, [user]);

  const set = (key: keyof typeof form) => (v: string) => setForm((f) => ({ ...f, [key]: v }));

  const isLegal = form.person_type === "legal";
  const phoneLocked = !!user?.phone_verified && !!user?.phone;

  // Hidden identity fields (per person type) always yield "" so they never block submit
  const fieldErrors = {
    email: validateEmail(form.email),
    phone: validatePhone(form.phone),
    postal_code: validatePostalCode(form.postal_code),
    name: isLegal ? "" : validatePersianText(form.name),
    family_name: isLegal ? "" : validatePersianText(form.family_name),
    national_code: isLegal ? "" : validateNationalCode(form.national_code),
    company_name: isLegal ? validatePersianText(form.company_name) : "",
    national_id: isLegal ? validateNationalId(form.national_id) : "",
  };

  const hasErrors = Object.values(fieldErrors).some(Boolean);

  // Invoice info completeness for the warning under the section title
  const invoiceInfoComplete = isLegal
    ? Boolean(form.company_name.trim() && form.national_id.trim())
    : Boolean(
        form.name.trim() &&
          form.family_name.trim() &&
          form.national_code.trim() &&
          form.postal_code.trim() &&
          form.phone.trim()
      );

   const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (hasErrors) return;
    // If phone is already verified it must not be changed - keep stored value
    const lockedPhone = phoneLocked ? user?.phone || form.phone : form.phone;
    // Send only the identity fields of the active person type; the other
    // side is cleared server-side so stale values never block validation.
    const payload =
      isLegal
        ? {
            email: form.email,
            phone: lockedPhone,
            postal_code: form.postal_code,
            person_type: form.person_type,
            company_name: form.company_name,
            national_id: form.national_id,
            name: "",
            family_name: "",
            national_code: "",
          }
        : {
            email: form.email,
            phone: lockedPhone,
            postal_code: form.postal_code,
            person_type: form.person_type,
            name: form.name,
            family_name: form.family_name,
            national_code: form.national_code,
            company_name: "",
            national_id: "",
          };
    update.mutate(payload);
  };

  const pwdSet = (key: keyof typeof pwdForm) => (v: string) =>
    setPwdForm((f) => ({ ...f, [key]: v }));

  const pwdValid =
    pwdForm.new.length >= 8 &&
    /[A-Z]/.test(pwdForm.new) &&
    /[0-9]/.test(pwdForm.new) &&
    pwdForm.new === pwdForm.confirm;

  const handlePassword = () => {
    if (!pwdValid) return;
    changePassword.mutate(
      {
        new_password: pwdForm.new,
        confirm_new_password: pwdForm.confirm,
      },
      {
        onSuccess: () => setPwdForm({ new: "", confirm: "" }),
      }
    );
  };

  // Subscription display logic
  const currentSub = subData?.current;
  const fixedSub = subData?.current_fixed;
  const { data: fixedUsage } = useSubscriptionUsage(Boolean(fixedSub));
  const fixedUsageData = fixedUsage as Record<string, unknown> | null | undefined;
  const isFree =
    !currentSub ||
    ["free", "رایگان"].includes((currentSub.plan_name || "").trim().toLowerCase());
  let subWarning: "expiring" | null = null;
  let daysLeft: number | null = null;
  if (currentSub?.end_date) {
    daysLeft = Math.ceil(
      (new Date(currentSub.end_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
    );
    if (daysLeft >= 0 && daysLeft < 7) subWarning = "expiring";
  }
  let fixedDaysLeft: number | null = null;
  if (fixedSub?.end_date) {
    fixedDaysLeft = Math.max(
      0,
      Math.ceil((new Date(fixedSub.end_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    );
  }
  const fixedLimit = Number((fixedUsageData as { monthly_token_limit?: unknown } | undefined)?.monthly_token_limit) || 0;
  const fixedUsed = Number((fixedUsageData as { used_tokens?: unknown } | undefined)?.used_tokens) || 0;
  const fixedRemaining = Number((fixedUsageData as { remaining_tokens?: unknown } | undefined)?.remaining_tokens) || 0;
  const fixedPct = Math.max(
    0,
    Math.min(
      100,
      Number((fixedUsageData as { used_percent?: unknown } | undefined)?.used_percent) ||
        (fixedLimit ? (fixedUsed / fixedLimit * 100) : 0)
    )
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h1 text-text-main">پروفایل</h1>
        <p className="text-body text-text-secondary mt-1">مدیریت اطلاعات حساب کاربری</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Identity Card */}
        <Card className="text-center flex flex-col">
          <div className="mx-auto w-20 h-20 rounded-2xl bg-primary-light flex items-center justify-center">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <h2 className="text-h2 text-text-main mt-4">
            {user?.name || user?.email}
            {user?.family_name ? ` ${user.family_name}` : ""}
          </h2>
          <p className="text-small text-text-tertiary mt-1 ltr" dir="ltr">{user?.email}</p>
          <div className="flex justify-center gap-2 mt-3">
            {user?.is_admin ? (
              <Badge variant="warning">مدیر</Badge>
            ) : (
              <Badge variant="success">کاربر فعال</Badge>
            )}
            {user?.email_verified && <Badge variant="info">ایمیل تأیید شده</Badge>}
          </div>
          <hr className="my-5 border-line" />
          <div className="space-y-3 text-right">
            <div className="flex justify-between">
              <span className="text-small text-text-tertiary">کد ملی</span>
              <span className="text-small text-text-main font-number" dir="ltr">
                {user?.national_code || "—"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-small text-text-tertiary">کد پستی</span>
              <span className="text-small text-text-main font-number" dir="ltr">
                {user?.postal_code || "—"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-small text-text-tertiary">تاریخ عضویت</span>
              <span className="text-small text-text-main">
                {user?.created_at
                  ? new Date(user.created_at).toLocaleDateString("fa-IR")
                  : "—"}
              </span>
            </div>
          </div>

          <div className="mt-auto pt-6">
            <DecorativeBanner />
          </div>
        </Card>

        {/* Edit Form */}
        <Card className="lg:col-span-2">
          <h2 className="text-h2 text-text-main">ویرایش اطلاعات</h2>
          {!isLoading && !invoiceInfoComplete && (
            <div className="mt-3 mb-5 flex items-start gap-2.5 rounded-btn bg-status-warning-bg p-3.5">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-status-warning-text shrink-0 mt-0.5">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <p className="text-small text-status-warning-text">
                بدون تکمیل اطلاعات زیر امکان دریافت فاکتور را ندارید؛ لطفاً موارد خواسته‌شده را تکمیل کنید.
              </p>
            </div>
          )}
          {isLoading ? (
            <div className={`space-y-3 ${invoiceInfoComplete ? "" : "mt-5"}`}>
              <div className="h-10 bg-surface-hover rounded animate-pulse" />
              <div className="h-10 bg-surface-hover rounded animate-pulse" />
              <div className="h-10 bg-surface-hover rounded animate-pulse" />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className={invoiceInfoComplete ? "" : "mt-5"}>
              <div className="space-y-5">
                <div>
                  <span className="block text-small font-medium text-text-secondary mb-1.5">نوع هویت</span>
                  <div className="grid grid-cols-2 gap-3 max-w-xs" role="radiogroup" aria-label="نوع هویت">
                    {(
                      [
                        { value: "real", label: "حقیقی" },
                        { value: "legal", label: "حقوقی" },
                      ] as { value: PersonType; label: string }[]
                    ).map((opt) => (
                      <label
                        key={opt.value}
                        className={`cursor-pointer select-none rounded-btn border py-2.5 px-4 text-small font-medium text-center transition-all ${
                          form.person_type === opt.value
                            ? "border-primary bg-primary-light text-primary"
                            : "border-line text-text-secondary hover:border-primary/40 hover:bg-surface-hover"
                        }`}
                      >
                        <input
                          type="radio"
                          name="person_type"
                          className="sr-only"
                          checked={form.person_type === opt.value}
                          onChange={() => setForm((f) => ({ ...f, person_type: opt.value }))}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {isLegal ? (
                    <>
                      <Field
                        label="نام شرکت"
                        value={form.company_name}
                        onChange={set("company_name")}
                        placeholder="نام شرکت را وارد کنید"
                        error={fieldErrors.company_name}
                      />
                      <Field
                        label="شناسه ملی"
                        value={form.national_id}
                        onChange={set("national_id")}
                        placeholder="۱۱ رقم"
                        ltr
                        error={fieldErrors.national_id}
                      />
                    </>
                  ) : (
                    <>
                      <Field
                        label="نام"
                        value={form.name}
                        onChange={set("name")}
                        placeholder="نام خود را وارد کنید"
                        error={fieldErrors.name}
                      />
                      <Field
                        label="نام خانوادگی"
                        value={form.family_name}
                        onChange={set("family_name")}
                        placeholder="نام خانوادگی خود را وارد کنید"
                        error={fieldErrors.family_name}
                      />
                    </>
                  )}
                <div>
                  <label className="block text-small font-medium text-text-secondary mb-1.5">ایمیل</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email")(e.target.value)}
                    placeholder="you@example.com"
                    className={`${fieldErrors.email ? inputErrorClass : inputClass} ltr text-left font-number`}
                    dir="ltr"
                  />
                  {fieldErrors.email && (
                    <p className="text-small text-status-error-text mt-1.5">{fieldErrors.email}</p>
                  )}
                  <VerifiedBox
                    verified={!!user?.email_verified && normalizeDigits(form.email.trim().toLowerCase()) === normalizeDigits((user?.email || "").trim().toLowerCase()) && !!form.email.trim()}
                    onClickVerify={() => {
                      const target = form.email.trim();
                      if (!target) {
                        toast.error("ابتدا ایمیل را وارد کنید");
                        return;
                      }
                      if (validateEmail(target)) {
                        toast.error(validateEmail(target));
                        return;
                      }
                      setVerifyField("email");
                    }}
                  />
                </div>
                <div>
                  <label className="block text-small font-medium text-text-secondary mb-1.5">شماره موبایل</label>
                  <input
                    type="text"
                    value={form.phone}
                    onChange={(e) => {
                      if (phoneLocked) return;
                      set("phone")(e.target.value);
                    }}
                    placeholder="۰۹۱۲..."
                    className={`${fieldErrors.phone ? inputErrorClass : inputClass} ltr text-left font-number ${phoneLocked ? "bg-surface-muted cursor-not-allowed opacity-80" : ""}`}
                    dir="ltr"
                    readOnly={phoneLocked}
                    disabled={phoneLocked}
                    title={phoneLocked ? "شماره موبایل تایید شده قابل تغییر نیست" : undefined}
                  />
                  {phoneLocked ? (
                    <p className="text-small text-text-tertiary mt-1.5">شماره موبایل تایید شده قابل تغییر نیست</p>
                  ) : (
                    fieldErrors.phone && (
                      <p className="text-small text-status-error-text mt-1.5">{fieldErrors.phone}</p>
                    )
                  )}
                  <VerifiedBox
                    verified={!!user?.phone_verified && !!user?.phone && normalizeDigits(form.phone.trim()) === normalizeDigits((user?.phone || "").trim())}
                    onClickVerify={() => {
                      if (phoneLocked) {
                        toast.error("شماره موبایل تایید شده قابل تغییر نیست");
                        return;
                      }
                      const target = form.phone.trim();
                      if (!target) {
                        toast.error("ابتدا شماره موبایل را وارد کنید");
                        return;
                      }
                      const err = validatePhone(target);
                      if (err) {
                        toast.error(err);
                        return;
                      }
                      setVerifyField("phone");
                    }}
                  />
                </div>
                {!isLegal && (
                  <Field
                    label="کد ملی"
                    value={form.national_code}
                    onChange={set("national_code")}
                    placeholder="۱۰ رقم"
                    ltr
                    error={fieldErrors.national_code}
                  />
                )}
                <Field
                  label="کد پستی"
                  value={form.postal_code}
                  onChange={set("postal_code")}
                  placeholder="۱۰ رقم"
                  ltr
                  error={fieldErrors.postal_code}
                />
                </div>
              </div>

              <div className="pt-2">
                <Button type="submit" loading={update.isPending} disabled={hasErrors} className="w-full md:w-auto">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                    <polyline points="17 21 17 13 7 13 7 21" />
                    <polyline points="7 3 7 8 15 8" />
                  </svg>
                  ذخیره تغییرات
                </Button>
              </div>
            </form>
          )}
        </Card>
      </div>

      {/* Password Reset + Subscription */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Password */}
        <Card>
          <div className="flex items-center gap-2.5 mb-5">
            <div className="w-9 h-9 rounded-lg bg-primary-light flex items-center justify-center">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <div>
              <h2 className="text-h2 text-text-main">تغییر رمز عبور</h2>
              <p className="text-small text-text-tertiary">حداقل ۸ کاراکتر، یک حرف بزرگ و یک عدد</p>
            </div>
          </div>

          <div className="space-y-4">
            <PasswordField label="رمز عبور جدید" value={pwdForm.new} onChange={pwdSet("new")} placeholder="••••••••" />
            <PasswordField label="تکرار رمز عبور جدید" value={pwdForm.confirm} onChange={pwdSet("confirm")} placeholder="••••••••" />

            {pwdForm.new && (
              <div className="space-y-1 text-tiny">
                <p className={pwdForm.new.length >= 8 ? "text-emerald-600" : "text-text-tertiary"}>
                  • حداقل ۸ کاراکتر
                </p>
                <p className={/[A-Z]/.test(pwdForm.new) ? "text-emerald-600" : "text-text-tertiary"}>
                  • حداقل یک حرف بزرگ
                </p>
                <p className={/[0-9]/.test(pwdForm.new) ? "text-emerald-600" : "text-text-tertiary"}>
                  • حداقل یک عدد
                </p>
                {pwdForm.confirm && (
                  <p className={pwdForm.new === pwdForm.confirm ? "text-emerald-600" : "text-red-500"}>
                    • تطابق رمزها
                  </p>
                )}
              </div>
            )}

            <Button
              className="w-full"
              onClick={handlePassword}
              loading={changePassword.isPending}
              disabled={!pwdValid}
            >
              تغییر رمز عبور
            </Button>
          </div>
        </Card>

        {/* Subscription */}
        <Card className="relative overflow-hidden">
          <div className="absolute top-0 left-0 w-32 h-32 bg-primary/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="relative">
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-9 h-9 rounded-lg bg-primary-light flex items-center justify-center">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                  <polygon points="12 2 2 7 12 12 22 7 12 2" />
                  <polyline points="2 17 12 22 22 17" />
                  <polyline points="2 12 12 17 22 12" />
                </svg>
              </div>
              <div>
                <h2 className="text-h2 text-text-main">اشتراک من</h2>
                <p className="text-small text-text-tertiary">وضعیت پلن فعلی شما</p>
              </div>
            </div>

            <div className="flex items-center gap-3 mb-4">
              <span className="text-h3 font-semibold text-text-main capitalize">
                {isFree ? "رایگان" : currentSub?.plan_name}
              </span>
              {isFree ? (
                <Badge variant="success">فعال</Badge>
              ) : currentSub?.is_active ? (
                subWarning === "expiring" ? (
                  <Badge variant="warning">در حال انقضا</Badge>
                ) : (
                  <Badge variant="success">فعال</Badge>
                )
              ) : (
                <Badge variant="error">منقضی شده</Badge>
              )}
            </div>

            {isFree ? (
              <p className="text-small text-text-tertiary mb-4">
                برای دسترسی به امکانات بیشتر، یکی از پلن‌های پولی را فعال کنید.
              </p>
            ) : currentSub?.end_date ? (
              <div className="space-y-1 mb-4">
                <p className="text-small text-text-secondary">
                  تاریخ انقضا:{" "}
                  <span className="font-number text-text-main" dir="ltr">
                    {new Date(currentSub.end_date).toLocaleDateString("fa-IR")}
                  </span>
                </p>
                {daysLeft !== null && daysLeft >= 0 && (
                  <p className="text-small text-text-tertiary">
                    {daysLeft === 0 ? "امروز منقضی می‌شود" : `${daysLeft.toLocaleString("fa-IR")} روز تا انقضا`}
                  </p>
                )}
              </div>
            ) : null}

            <div className="flex gap-3">
              {isFree ? (
                <Button className="flex-1" onClick={() => navigate("/subscription")}>
                  مشاهده پلن‌ها
                </Button>
              ) : subWarning === "expiring" ? (
                <Button className="flex-1" onClick={() => navigate("/subscription")}>
                  تمدید اشتراک
                </Button>
              ) : (
                <div className="flex-1 py-2.5 rounded-btn border border-line bg-surface-hover text-center text-small font-medium text-text-tertiary">
                  اشتراک فعلی
                </div>
              )}
              <Button variant="secondary" onClick={() => setHistoryOpen(true)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                تاریخچه
              </Button>
            </div>

            {/* Fixed-rate plan info (when the user also holds this plan) */}
            {fixedSub && (
              <div className="mt-6 pt-4 border-t border-line">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-primary-light flex items-center justify-center">
                    <FixedPlanIcon />
                  </div>
                  <div>
                    <h3 className="text-h3 text-text-main">اشتراک تعرفه ثابت</h3>
                    <p className="text-small text-text-tertiary">پلن قیمت ثابت / ماهانه</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 mb-3">
                <span className="text-base font-semibold text-text-main capitalize">{toPersianDigits(fixedSub.plan_name)}</span>
                  <Badge variant="success">فعال</Badge>
                </div>

                <div className="space-y-1 mb-3">
                  {fixedSub.start_date && (
                    <p className="text-small text-text-secondary">
                      شروع:{" "}
                      <span className="font-number text-text-main" dir="ltr">
                        {new Date(fixedSub.start_date).toLocaleDateString("fa-IR")}
                      </span>
                    </p>
                  )}
                  {fixedSub.end_date && (
                    <p className="text-small text-text-secondary">
                      انقضا:{" "}
                      <span className="font-number text-text-main" dir="ltr">
                        {new Date(fixedSub.end_date).toLocaleDateString("fa-IR")}
                      </span>
                    </p>
                  )}
                  {fixedDaysLeft !== null && (
                    <p className="text-small text-text-tertiary">
                      {fixedDaysLeft === 0
                        ? "امروز منقضی می‌شود"
                        : `${toPersianDigits(String(fixedDaysLeft))} روز باقی مانده`}
                    </p>
                  )}
                </div>

                {fixedUsageData && fixedLimit > 0 && (
                  <div className="mt-1">
                    <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "rgb(var(--chart-blue) / 0.15)" }}>
                      <div className="h-full" style={{ width: `${Math.round(fixedPct)}%`, background: fixedPct >= 90 ? "#dc2626" : "rgb(var(--chart-blue))" }} />
                    </div>
                    <p className="text-small text-text-tertiary mt-1.5">
                      {formatNumber(fixedRemaining)} توکن باقیمانده از {formatNumber(fixedLimit)}
                    </p>
                  </div>
                )}

                <Button className="w-full mt-3" variant="secondary" onClick={() => navigate("/subscription")}>
                  مشاهده پلن‌ها
                </Button>
              </div>
            )}
          </div>
        </Card>
      </div>

      <VerificationModal
        open={verifyField !== null}
        onClose={() => setVerifyField(null)}
        field={verifyField || "email"}
        destination={
          verifyField === "phone"
            ? (form.phone.trim() || user?.phone || "")
            : (form.email.trim() || user?.email || "")
        }
      />

      <SubscriptionHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
    </div>
  );
}
